import agGridCSSUrl from '@ag-grid-community/styles/ag-grid.css?url'
import agGridQuartzThemeUrl from '@ag-grid-community/styles/ag-theme-quartz.css?url'
import {
	json,
	type LoaderFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
} from '@remix-run/node'
import {
	Form,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useMatches,
	useSubmit,
} from '@remix-run/react'
import { withSentry } from '@sentry/remix'
import katexStylesUrl from 'katex/dist/katex.min.css?url'
import { useRef } from 'react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import sonnerStyles from 'sonner/dist/styles.css?url'
import { Confetti } from './components/confetti.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { Logo } from './components/logo'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuPortal,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from './components/ui/dropdown-menu.tsx'
import { Icon, href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import remixImageStylesUrl from './remix-image/remix-image.css?url'
import { ThemeSwitch, useTheme } from './routes/resources+/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getImpersonator, getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { getConfetti } from './utils/confetti.server.ts'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getUserImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser, userHasRole, useUser } from './utils/user.ts'
import { type IconName } from '@/icon-name'

export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', type: 'image/svg+xml', href: iconsHref, as: 'image' },
		// { rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
		{
			rel: 'alternate icon',
			type: 'image/png',
			href: '/favicons/favicon-32x32.png',
		},
		{ rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		// { rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
		{ rel: 'stylesheet', href: remixImageStylesUrl },
		{ rel: 'stylesheet', href: agGridCSSUrl },
		{ rel: 'stylesheet', href: agGridQuartzThemeUrl },
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		{ rel: 'stylesheet', href: sonnerStyles },
		{ rel: 'stylesheet', href: katexStylesUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{ title: data ? 'Marky' : 'Error | Marky' },
		{ name: 'description', content: `Your own captain's log` },
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { id: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const { confettiId, headers: confettiHeaders } = getConfetti(request)
	const honeyProps = honeypot.getInputProps()

	const impersonator = (await getImpersonator(request))?.user

	return json(
		{
			user,
			impersonator,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			confettiId,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
				confettiHeaders,
			),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
	allowIndexing = true,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
	allowIndexing?: boolean
}) {
	return (
		<html
			lang="en"
			className={`${theme} h-full overflow-x-hidden antialiased scrollbar-gutter-auto scrollbar-none`}
		>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				{env.MODE === 'production' ? (
					<>
						<script
							nonce={nonce}
							defer
							src="https://cloud.umami.is/script.js"
							data-website-id="cd80352e-8468-4bdc-8e9e-ce11d5fcc378"
						></script>
						<script
							nonce={nonce}
							dangerouslySetInnerHTML={{
								__html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
					new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
					j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
					'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
					})(window,document,'script','dataLayer','GTM-PCLW48SQ');`,
							}}
						></script>
					</>
				) : null}

				<Links />
			</head>
			<body className="h-full bg-background text-foreground">
				{env.MODE === 'production' ? (
					<noscript>
						<iframe
							src="https://www.googletagmanager.com/ns.html?id=GTM-PCLW48SQ"
							height="0"
							width="0"
							style={{ display: 'none', visibility: 'hidden' }}
						></iframe>
					</noscript>
				) : null}

				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	const user = useOptionalUser()
	const theme = useTheme()
	const matches = useMatches()

	const userIsStudent = user ? userHasRole(user, 'student') : false
	// const isOnSearchPage = matches.find((m) => m.id === 'routes/users+/index')
	// const searchBar = isOnSearchPage ? null : <SearchBar status="idle" />
	const showHeader = matches.find((m) => m.id === 'routes/_marketing+/index')
	const allowIndexing = data.ENV.ALLOW_INDEXING !== 'false'
	useToast(data.toast)

	return (
		<Document
			nonce={nonce}
			theme={theme}
			allowIndexing={allowIndexing}
			env={data.ENV}
		>
			<div className="flex h-[calc(100%-88px)] flex-col justify-between">
				{showHeader ? (
					<header className="bg-red px-4 py-6 sm:container">
						<nav className="flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
							<Logo className="w-32 sm:w-48" />
							{/* <div className="ml-auto hidden max-w-sm flex-1 sm:block">
								{searchBar}
							</div> */}
							<div className="flex items-center justify-end gap-2 sm:gap-4">
								<ThemeSwitch
									userPreference={data.requestInfo.userPrefs.theme}
								/>
								{user ? (
									<>
										<UserDropdown loaderData={data} />

										{userIsStudent ? (
											<Button
												asChild
												variant="default"
												size={'sm'}
												className="bg-orange-500"
											>
												<Link to="/student/dashboard">
													<Icon name="home" className="mr-2 hidden sm:block" />{' '}
													Home
												</Link>
											</Button>
										) : (
											<Button
												asChild
												variant="default"
												size={'lg'}
												className="bg-orange-500"
											>
												<Link to="/app/tests">Tests</Link>
											</Button>
										)}
									</>
								) : (
									<Button
										asChild
										variant="default"
										size="lg"
										className="bg-orange-500"
									>
										<Link to="/login">Log In</Link>
									</Button>
								)}
							</div>
						</nav>
					</header>
				) : null}

				<div className="flex-1">
					<Outlet />
				</div>
			</div>
			<Confetti id={data.confettiId} />
			<EpicToaster position="top-right" closeButton theme={theme} />
			<EpicProgress />
		</Document>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default withSentry(AppWithProviders)

interface MenuItem {
	to: string
	icon: IconName
	label: string
	roles: string[]
}

const menuItems: MenuItem[] = [
	{
		to: '/student/dashboard',
		icon: 'home',
		label: 'Home',
		roles: ['student'],
	},
	{
		to: '/app/dashboard',
		icon: 'dashboard',
		label: 'Dashboard',
		roles: ['academy', 'admin'],
	},
	{
		to: '/student/profile',
		icon: 'file-text',
		label: 'Profile',
		roles: ['student'],
	},
	{
		to: '/app/tests',
		icon: 'file-text',
		label: 'Tests',
		roles: ['academy', 'admin'],
	},
	{
		to: '/admin/users',
		icon: 'lock-closed',
		label: 'Manage Users',
		roles: ['admin'],
	},
]

export function UserDropdown({ loaderData }: { loaderData: any }) {
	const user = useUser()
	const submit = useSubmit()
	const logoutFormRef = useRef<HTMLFormElement>(null)

	const userRoles = user.roles.map((role) => role.name)

	const filteredMenuItems = menuItems
		.filter((item) => item.roles.some((role) => userRoles.includes(role)))
		.reduce((acc, item) => {
			if (!acc.some((existing) => existing.to === item.to)) {
				acc.push(item)
			}
			return acc
		}, [] as MenuItem[])

	const impersonator = loaderData?.impersonator

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button className="relative flex rounded-full bg-white p-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
					<span className="absolute -inset-1.5" />
					<span className="sr-only">Open user menu</span>
					<img
						alt={user.name ?? user.username}
						src={getUserImgSrc(user.image?.id)}
						className="h-8 w-8 rounded-full"
					/>
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					<DropdownMenuLabel>
						<span className="text-body-sm">
							Hey, {user.name ?? user.username}
						</span>
					</DropdownMenuLabel>
					<DropdownMenuSeparator />
					{impersonator && (
						<DropdownMenuItem asChild>
							<Form action="/impersonate" method="POST">
								<input type="hidden" name="intent" value="stop" />
								<Button>
									<Icon className="text-body-md" name="exit">
										Stop Impersonating {user.name ?? ''}
									</Icon>
								</Button>
							</Form>
						</DropdownMenuItem>
					)}
					{filteredMenuItems.map((item, index) => (
						<DropdownMenuItem key={index} asChild>
							<Link prefetch="intent" to={item.to}>
								<Icon className="text-body-md" name={item.icon}>
									{item.label}
								</Icon>
							</Link>
						</DropdownMenuItem>
					))}
					<DropdownMenuItem
						asChild
						onSelect={(event) => {
							event.preventDefault()
							submit(logoutFormRef.current)
						}}
					>
						<Form action="/logout" method="POST" ref={logoutFormRef}>
							<Icon className="text-body-md" name="exit">
								<button type="submit">Logout</button>
							</Icon>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}
